import { memo } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TooltipPortal } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { SidebarNavItem } from './SidebarNavItem';
import type { NavGroup } from './navigation-config';

interface SidebarNavGroupProps {
  group: NavGroup;
  isExpanded: boolean;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onNavClick: (href: string) => void;
}

export const SidebarNavGroup = memo(function SidebarNavGroup({
  group,
  isExpanded,
  isCollapsed,
  onToggleCollapse,
  onNavClick,
}: SidebarNavGroupProps) {
  const location = useLocation();
  const GroupIcon = group.icon;

  const hasActiveChild = group.items.some((item) => item.isActive(location.pathname));
  const isGroupActive = group.isActive?.(location.pathname) ?? hasActiveChild;

  // Collapsed sidebar mode: show group icon as a tooltip trigger
  if (!isExpanded) {
    return (
      <div className="space-y-1">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={isGroupActive ? 'default' : 'ghost'}
                className={cn(
                  'w-full flex items-center justify-center h-9 px-0 relative',
                  isGroupActive && 'bg-[#F37022] hover:bg-[#E36012]',
                  !isGroupActive && 'dark:text-gray-200 dark:hover:bg-gray-700',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1'
                )}
                onClick={() => group.href ? onNavClick(group.href) : onToggleCollapse()}
                aria-expanded={!isCollapsed}
                aria-label={`${group.label} ${isCollapsed ? 'expand' : 'collapse'}`}
              >
                <GroupIcon className="h-4 w-4 shrink-0" />
                {group.showDot && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full" aria-label="New activity" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent
                side="right"
                className="bg-gray-900 text-white border-0 dark:border-gray-700"
                style={{ zIndex: 99999 }}
              >
                {group.label}
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  // Expanded sidebar mode: group header + collapsible children
  // When group.href is set, the label navigates and only the chevron toggles collapse.
  // When no href, the entire header (label + chevron) toggles collapse.
  return (
    <Collapsible open={!isCollapsed} onOpenChange={() => onToggleCollapse()}>
      <div className="flex items-center gap-1">
        {group.href ? (
          <Button
            variant="ghost"
            className={cn(
              'flex-1 flex items-center gap-2 transition-all h-9 justify-start px-3',
              'dark:text-gray-200 dark:hover:bg-gray-700',
              isGroupActive && 'text-[#F37022] dark:text-orange-300',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1'
            )}
            onClick={() => {
              if (isCollapsed) onToggleCollapse();
              onNavClick(group.href!);
            }}
          >
            <GroupIcon className="h-4 w-4 shrink-0" />
            <span className="transition-opacity duration-300 text-sm">
              {group.label}
            </span>
            {group.showDot && (
              <span className="w-2 h-2 bg-orange-500 rounded-full shrink-0" aria-label="New activity" />
            )}
          </Button>
        ) : (
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                'flex-1 flex items-center gap-2 transition-all h-9 justify-start px-3',
                'dark:text-gray-200 dark:hover:bg-gray-700',
                isGroupActive && 'text-[#F37022] dark:text-orange-300',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1'
              )}
            >
              <GroupIcon className="h-4 w-4 shrink-0" />
              <span className="transition-opacity duration-300 text-sm">
                {group.label}
              </span>
              {group.showDot && (
                <span className="w-2 h-2 bg-orange-500 rounded-full shrink-0" aria-label="New activity" />
              )}
            </Button>
          </CollapsibleTrigger>
        )}
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'h-11 w-11 md:h-9 md:w-9 transition-all dark:text-gray-200 dark:hover:bg-gray-700',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1'
            )}
            aria-expanded={!isCollapsed}
            aria-label={`${group.label} ${isCollapsed ? 'expand' : 'collapse'}`}
          >
            <ChevronRight
              className={cn(
                'h-4 w-4 transition-transform duration-200',
                !isCollapsed && 'rotate-90'
              )}
            />
          </Button>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
        <div className="pl-6 space-y-0.5">
          {group.items.map((item) => (
            <SidebarNavItem
              key={item.href}
              item={item}
              isExpanded={true}
              onClick={onNavClick}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
});
